import React, { useCallback, useState } from "react";

import { PrimaryButton } from "~/components/common/buttons";
import { InputLabel, TextInput } from "~/components/common/inputs";

interface ZipDownloadLookupPageProps {
  orgSlug: string;
}

// The CSRF cookie is HttpOnly (JS can't read document.cookie for it), so the token travels via
// a <meta> tag instead -- same extraction as ~/api/client.ts's Apollo link and
// useUploadManager.tsx's plain-XHR uploads. Read fresh per call rather than importing either of
// those modules' copies -- this page has no other reason to depend on either setup.
const getCsrfToken = () =>
  document
    .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    ?.getAttribute("content") ?? null;

/**
 * Hidden page -- reachable only by direct URL (/:orgSlug/admin/zip-download-lookup), not linked
 * from AdminPage's own nav tabs. Lets an org:admin retrieve a specific historical zip download
 * belonging to their own org by request id, e.g. to answer a customer support "what exactly did
 * this buyer download" question. The actual authorization (is this user org:admin for
 * orgSlug, does the id belong to this org) happens server-side in
 * api.views.ZipDownloadOrgAdminLookupAPI -- this page has no permission logic of its own.
 */
export const ZipDownloadLookupPage = ({
  orgSlug,
}: ZipDownloadLookupPageProps) => {
  const [requestId, setRequestId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setSubmitting(true);
      setError(null);

      try {
        const csrfToken = getCsrfToken();
        const response = await fetch(
          `/api/orgs/${orgSlug}/zip-download-lookup/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
            },
            credentials: "same-origin",
            body: JSON.stringify({ request_id: requestId.trim() }),
          }
        );

        const data = await response.json().catch(() => null);

        if (!response.ok || !data?.download_url) {
          setError(data?.error ?? "Something went wrong. Please try again.");
          return;
        }

        // A direct cross-origin navigation to the presigned S3 url, same as the ordinary zip
        // download flow's own interstitial page -- not a client-side route change.
        window.location.href = data.download_url;
      } catch {
        setError("Something went wrong. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [orgSlug, requestId]
  );

  return (
    <div>
      <h2>Zip Download Lookup</h2>
      <p>
        Enter a zip download request ID belonging to this organization to
        retrieve the original archive.
      </p>
      <form onSubmit={submit}>
        <InputLabel htmlFor="zip-download-request-id">
          Request ID
          <TextInput
            id="zip-download-request-id"
            type="text"
            value={requestId}
            onChange={(event) => setRequestId(event.target.value)}
            disabled={submitting}
          />
        </InputLabel>
        {error && <p style={{ color: "#c0392b" }}>{error}</p>}
        <PrimaryButton
          type="submit"
          disabled={submitting || !requestId.trim()}
          style={{ marginTop: "16px" }}
        >
          Look up
        </PrimaryButton>
      </form>
    </div>
  );
};
